"use client";

import { useEffect, useRef } from "react";
import { hero } from "@/content";
import Ticker from "./Ticker";

/** Hero art with a slow scroll parallax; static under prefers-reduced-motion. */
export default function Hero() {
  const layer = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const y = Math.min(window.scrollY, 900);
        if (layer.current) layer.current.style.transform = `translateY(${y * 0.22}px) scale(1.04)`;
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section id="top" className="relative isolate overflow-hidden">
      {/* IMAGE SLOT: /public/img/hero-town.png — painterly lantern-lit town at dusk. */}
      <div ref={layer} className="absolute inset-0 -z-10 will-change-transform">
        {/* eslint-disable-next-line @next/next/no-img-element -- local static art, no optimization pipeline needed for a one-page site */}
        <img
          src={hero.art.src}
          alt={hero.art.alt}
          className="h-full w-full object-cover"
          fetchPriority="high"
        />
      </div>
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 bg-gradient-to-b from-ink-950/70 via-ink-950/35 to-ink-950"
      />

      <div className="mx-auto flex min-h-[92svh] max-w-6xl flex-col justify-center px-4 pb-20 pt-28 sm:px-6">
        <h1 className="max-w-3xl font-display text-5xl font-semibold leading-[1.04] tracking-tight text-parch-100 sm:text-6xl lg:text-7xl">
          {hero.headline}
        </h1>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-parch-200 sm:text-lg">
          {hero.sub}
        </p>

        <div className="mt-9 flex flex-wrap items-center gap-3">
          <a
            href="#worlds"
            className="rounded-lg bg-ember-400 px-6 py-3 text-base font-semibold text-ink-950 shadow-[0_0_28px_rgba(231,156,61,0.25)] transition-all hover:-translate-y-0.5 hover:bg-ember-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ember-300"
          >
            {hero.ctas.primary}
          </a>
          <a
            href="#windows"
            className="rounded-lg border border-ember-500/70 px-6 py-3 text-base font-semibold text-ember-300 transition-colors hover:bg-ember-500/10"
          >
            {hero.ctas.secondary}
          </a>
          <a
            href="#footer"
            className="px-2 py-3 text-base text-parch-300 underline decoration-parch-400/40 underline-offset-4 transition-colors hover:text-parch-100"
          >
            {hero.ctas.tertiary} ↗
          </a>
        </div>

        <div className="mt-10">
          <Ticker />
        </div>
      </div>
    </section>
  );
}
